import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import { mission, objectives } from '../../data/mock';
import { BookOpen, Sparkles, Gift, Heart, ArrowLeft, Leaf, TreePine } from 'lucide-react';
import { Button } from '../ui/button';

const iconMap = {
  BookOpen: BookOpen,
  Sparkles: Sparkles,
  Gift: Gift,
  Heart: Heart
};

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-sky-50">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="relative py-16 overflow-hidden">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-10 left-10 w-32 h-32 bg-emerald-200 rounded-full blur-3xl" />
            <div className="absolute bottom-10 right-10 w-40 h-40 bg-amber-200 rounded-full blur-3xl" />
          </div>
          
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <Link to="/" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 mb-8 group">
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
              Back to Home
            </Link>
            
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-emerald-100 rounded-2xl">
                <TreePine className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-serif font-bold text-slate-800">
                About Us
              </h1>
            </div>
            
            <p className="text-xl text-slate-600 leading-relaxed">
              Learn about our mission to nurture faith in children through engaging, biblically-sound resources.
            </p>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-12 border border-emerald-100">
              <div className="flex items-center gap-3 mb-6">
                <Leaf className="w-6 h-6 text-emerald-500" />
                <h2 className="text-2xl font-serif font-bold text-slate-800">{mission.title}</h2>
              </div>
              
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                {mission.description}
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-emerald-50 rounded-2xl p-6">
                  <h3 className="font-bold text-emerald-800 mb-3">Our Vision</h3>
                  <p className="text-slate-600">{mission.vision}</p>
                </div>
                <div className="bg-amber-50 rounded-2xl p-6">
                  <h3 className="font-bold text-amber-800 mb-3">Our Approach</h3>
                  <p className="text-slate-600">{mission.approach}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Objectives Section */}
        <section className="py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-serif font-bold text-slate-800 text-center mb-12">
              Our Objectives
            </h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {objectives.map((obj) => {
                const Icon = iconMap[obj.icon] || BookOpen;
                return (
                  <div
                    key={obj.id}
                    className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-slate-100 group"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-sky-100 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="w-7 h-7 text-emerald-600" />
                    </div>
                    <h3 className="font-bold text-slate-800 mb-2">{obj.title}</h3>
                    <p className="text-sm text-slate-600">{obj.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl p-8 sm:p-12 text-center text-white">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold mb-4">
                Join Our Mission
              </h2>
              <p className="text-emerald-100 mb-8 max-w-2xl mx-auto">
                Partner with us to bring faith-based resources to children everywhere.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/login">
                  <Button className="bg-white text-emerald-600 hover:bg-emerald-50 rounded-full px-8 py-6">
                    Get Started
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
